// apps/office/src/pages/PartnerVendorPayments.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Loader2, Eye, X, Users, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { formatCurrency } from '../../lib/utils';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

export default function PartnerVendorPayments() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [vendorSummary, setVendorSummary] = useState({});
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const partners = await pb.collection('partners').getFullList({
          filter: `createdBy = "${user.id}" || email = "${user.email}"`,
        });
        if (!partners.length) {
          setLoading(false);
          return;
        }
        const partner = partners[0];

        const assignments = await pb.collection('partner_site_assignments').getFullList({
          filter: `partner = "${partner.id}" && isActive = true`,
          expand: 'site',
        });
        const siteIds = assignments.map(a => a.expand?.site?.id).filter(Boolean);

        if (!siteIds.length) {
          setLoading(false);
          return;
        }

        const allLabours = await pb.collection('labour').getFullList({
          filter: `site ~ "${siteIds.join('||')}" && status = "active"`,
          expand: 'vendor',
        });
        const vendorIds = [...new Set(allLabours.map(l => l.vendor).filter(Boolean))];

        if (!vendorIds.length) {
          setLoading(false);
          return;
        }

        const vendorRecords = await pb.collection('vendors').getFullList({
          filter: vendorIds.map(id => `id="${id}"`).join('||'),
          sort: 'name',
        });

        const [allBills, allPayments] = await Promise.all([
          pb.collection('vendor_bills').getFullList({
            filter: `vendor ~ "${vendorIds.join('||')}" && project ~ "${siteIds.join('||')}"`,
            expand: 'project',
          }),
          pb.collection('vendor_payments').getFullList({
            filter: `vendor ~ "${vendorIds.join('||')}" && project ~ "${siteIds.join('||')}"`,
            expand: 'project',
          }),
        ]);

        const summary = {};
        vendorRecords.forEach(v => {
          const vendorBills = allBills.filter(b => b.vendor === v.id);
          const vendorPayments = allPayments.filter(p => p.vendor === v.id);
          const totalBills = vendorBills.reduce((sum, b) => sum + (b.billAmount || 0), 0);
          const totalPaid = vendorPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
          const totalDue = totalBills - totalPaid;
          summary[v.id] = {
            vendor: v,
            bills: vendorBills.sort((a, b) => new Date(b.billDate) - new Date(a.billDate)),
            payments: vendorPayments.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate)),
            totalBills,
            totalPaid,
            totalDue,
            billCount: vendorBills.length,
            paymentCount: vendorPayments.length,
          };
        });

        setVendors(vendorRecords);
        setVendorSummary(summary);
        setFilteredVendors(vendorRecords);
      } catch (error) {
        console.error('Error fetching vendor data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchData();
  }, [user]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredVendors(vendors);
    } else {
      const lower = searchTerm.toLowerCase();
      setFilteredVendors(vendors.filter(v => v.name?.toLowerCase().includes(lower)));
    }
  }, [searchTerm, vendors]);

  const totalVendors = vendors.length;
  const totalBills = vendors.reduce((sum, v) => sum + (vendorSummary[v.id]?.totalBills || 0), 0);
  const totalPaid = vendors.reduce((sum, v) => sum + (vendorSummary[v.id]?.totalPaid || 0), 0);
  const totalDue = totalBills - totalPaid;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const openDetailModal = (vendor) => {
    setSelectedVendor(vendor);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedVendor(null);
  };

  return (
    <div className="space-y-6 p-4 md:p-6 bg-slate-50/50 min-h-screen">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-amber-500" />
            Vendor Bills & Payments
          </h1>
          <p className="text-slate-500 text-sm">Vendor-wise summary across your assigned sites</p>
        </div>
        <div className="text-xs text-slate-400 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
          ?? Read-only
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Total Vendors</p>
              <p className="text-2xl font-bold text-slate-800">{totalVendors}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Total Bills</p>
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalBills)}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-full">
              <TrendingUp className="w-6 h-6 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Total Paid</p>
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalPaid)}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-full">
              <TrendingUp className="w-6 h-6 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-rose-500 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold">Total Due</p>
              <p className={`text-2xl font-bold ${totalDue > 0 ? 'text-rose-600' : 'text-green-600'}`}>
                {formatCurrency(totalDue)}
              </p>
            </div>
            <div className="p-3 bg-rose-50 rounded-full">
              <TrendingDown className="w-6 h-6 text-rose-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={() => setSearchTerm('')}>Clear</Button>
      </div>

      <Card className="shadow-sm border-0">
        <CardContent className="p-0 overflow-x-auto">
          {filteredVendors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <h3 className="text-lg font-semibold text-slate-700">No Vendors Found</h3>
              <p className="text-slate-400 text-sm">No vendors associated with your assigned sites.</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Vendor</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Total Bills</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Total Paid</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Due</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredVendors.map(vendor => {
                  const summary = vendorSummary[vendor.id] || { totalBills: 0, totalPaid: 0, totalDue: 0, billCount: 0, paymentCount: 0 };
                  return (
                    <tr key={vendor.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {vendor.photo && (
                            <img
                              src={pb.files.getUrl(vendor, vendor.photo)}
                              alt={vendor.name}
                              className="w-8 h-8 rounded-full object-cover border"
                            />
                          )}
                          <div>
                            <p className="font-medium text-slate-800">{vendor.name}</p>
                            <p className="text-xs text-slate-400">
                              {summary.billCount} bills � {summary.paymentCount} payments
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(summary.totalBills)}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatCurrency(summary.totalPaid)}</td>
                      <td className={`px-4 py-3 text-right font-mono font-bold ${summary.totalDue > 0 ? 'text-rose-600' : 'text-green-600'}`}>
                        {formatCurrency(summary.totalDue)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:bg-blue-50 border-blue-200 gap-1"
                          onClick={() => openDetailModal(vendor)}
                        >
                          <Eye className="w-4 h-4" /> View Details
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {filteredVendors.length > 0 && (
                <tfoot className="bg-slate-50 border-t font-semibold">
                  <tr>
                    <td className="px-4 py-3 text-slate-700">Total ({filteredVendors.length} vendors)</td>
                    <td className="px-4 py-3 text-right font-mono text-amber-600">
                      {formatCurrency(filteredVendors.reduce((sum, v) => sum + (vendorSummary[v.id]?.totalBills || 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-600">
                      {formatCurrency(filteredVendors.reduce((sum, v) => sum + (vendorSummary[v.id]?.totalPaid || 0), 0))}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono font-bold ${
                      filteredVendors.reduce((sum, v) => sum + (vendorSummary[v.id]?.totalDue || 0), 0) > 0
                        ? 'text-rose-600'
                        : 'text-green-600'
                    }`}>
                      {formatCurrency(filteredVendors.reduce((sum, v) => sum + (vendorSummary[v.id]?.totalDue || 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-center"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </CardContent>
      </Card>

      {showDetailModal && selectedVendor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-3">
                  {selectedVendor.photo && (
                    <img
                      src={pb.files.getUrl(selectedVendor, selectedVendor.photo)}
                      alt={selectedVendor.name}
                      className="w-10 h-10 rounded-full object-cover border"
                    />
                  )}
                  {selectedVendor.name}
                </h2>
                <p className="text-sm text-slate-500">
                  {selectedVendor.type || 'Vendor'} � {selectedVendor.phone || 'No phone'}
                </p>
              </div>
              <button onClick={closeDetailModal} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {(() => {
              const summary = vendorSummary[selectedVendor.id] || { totalBills: 0, totalPaid: 0, totalDue: 0 };
              return (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-slate-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-slate-500">Total Bills</p>
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(summary.totalBills)}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-slate-500">Total Paid</p>
                    <p className="text-lg font-bold text-emerald-600">{formatCurrency(summary.totalPaid)}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-slate-500">Due</p>
                    <p className={`text-lg font-bold ${summary.totalDue > 0 ? 'text-rose-600' : 'text-green-600'}`}>
                      {formatCurrency(summary.totalDue)}
                    </p>
                  </div>
                </div>
              );
            })()}

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-500" />
                  Bills ({vendorSummary[selectedVendor.id]?.bills?.length || 0})
                </h3>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Bill #</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Project</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Description</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(vendorSummary[selectedVendor.id]?.bills || []).length === 0 ? (
                        <tr><td colSpan="5" className="px-3 py-4 text-center text-slate-400">No bills</td></tr>
                      ) : (
                        (vendorSummary[selectedVendor.id]?.bills || []).map(b => (
                          <tr key={b.id} className="hover:bg-slate-50/50">
                            <td className="px-3 py-2">{formatDate(b.billDate)}</td>
                            <td className="px-3 py-2 font-mono text-xs">{b.billNumber || '-'}</td>
                            <td className="px-3 py-2">{b.expand?.project?.name || 'N/A'}</td>
                            <td className="px-3 py-2 text-xs">{b.description || '-'}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold">{formatCurrency(b.billAmount || 0)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {vendorSummary[selectedVendor.id]?.bills?.length > 0 && (
                      <tfoot className="bg-slate-50 border-t font-semibold">
                        <tr>
                          <td colSpan="4" className="px-3 py-2 text-right">Total</td>
                          <td className="px-3 py-2 text-right font-mono text-amber-600">
                            {formatCurrency((vendorSummary[selectedVendor.id]?.bills || []).reduce((s, b) => s + (b.billAmount || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-emerald-500" />
                  Payments ({vendorSummary[selectedVendor.id]?.payments?.length || 0})
                </h3>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Project</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Method</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Transaction</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(vendorSummary[selectedVendor.id]?.payments || []).length === 0 ? (
                        <tr><td colSpan="5" className="px-3 py-4 text-center text-slate-400">No payments</td></tr>
                      ) : (
                        (vendorSummary[selectedVendor.id]?.payments || []).map(p => (
                          <tr key={p.id} className="hover:bg-slate-50/50">
                            <td className="px-3 py-2">{formatDate(p.paymentDate)}</td>
                            <td className="px-3 py-2">{p.expand?.project?.name || 'N/A'}</td>
                            <td className="px-3 py-2">
                              <Badge className="bg-indigo-50 text-indigo-700">{p.paymentMethod?.replace('_', ' ')}</Badge>
                            </td>
                            <td className="px-3 py-2 text-xs">{p.transactionId || '-'}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold text-emerald-600">
                              {formatCurrency(p.amount || 0)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {vendorSummary[selectedVendor.id]?.payments?.length > 0 && (
                      <tfoot className="bg-slate-50 border-t font-semibold">
                        <tr>
                          <td colSpan="4" className="px-3 py-2 text-right">Total</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-600">
                            {formatCurrency((vendorSummary[selectedVendor.id]?.payments || []).reduce((s, p) => s + (p.amount || 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t flex justify-between items-center">
              <p className="text-xs text-slate-400 flex items-center gap-2">
                <Eye className="w-4 h-4" /> View-only access
              </p>
              <Button variant="outline" onClick={closeDetailModal}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}